import React, { useState, useEffect, useRef } from 'react';
import styled, { css } from 'styled-components';
import parse from 'html-react-parser';
import { _ } from '_locales';

import { Drawer } from '_components';
import { isRTL, eventTracker } from '_helpers';

import Slick from 'react-slick';

import useMaterialDetails from './hooks';
import { Description } from './components';
import { LoadingSpinner } from '../../components';

import apis from '../../app/apis';

/**
 * import styles
 */
const parentWidth = document.getElementById('rcroot').getBoundingClientRect().width;

const Title = styled.h2`
  font-size: 16px !important;
  display: block;
   
  direction: ${() => (isRTL() ? 'rtl' : 'ltr')};
  margin-bottom: 7px !important;
  text-align: left;
`;

const DepotContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: 35px 0;
`;

const Drawers = styled.ul`
  margin: 30px 0;
  padding: 0;
`;

const Container = styled.div`
  width: 100%;
  height: 100%;
  text-align: center;
  margin: 0 auto;
  position: relative;
  background-color: #fff;
  padding: 0;

  &.content-container-responsive {
    width: calc(100% - 195px);
  }
`;

const ContentContainer = styled.div`
  ${(parentWidth < 551) && css`
    .content-container-responsive {
      width: 100% !important;
      padding: 0 20px !important;
      box-sizing: border-box !important;
    }
  `};
`;

const ImageWrapper = styled.div`
  text-align: center;
`;

const ImageContainer = styled.div`
  width: calc(100% - 100px);
  position: relative;
  left: 50%;
  transform: translateX(-50%);

  @media screen and (max-width: 500px) {
    width: 100% !important;
  }

  ${(parentWidth < 501) && css`
    width: 100% !important;
  `};
`;

const ProgressImage = styled.div`
  width: 100%;
  min-height: 200px;
  object-fit: cover;
  background-image: url(${({ src }) => src});
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
`;

const Img = styled.img`
  width: 100% !important;
  max-width: 240px !important;
  height: auto !important;
  min-height: 180px !important;
`;

const Wrapper = styled.div`
  max-width: 240px;
  width: 100%;
  min-height: 180px;
  overflow: hidden;
  margin: 0 auto;
`;

const ArrowsContainer = styled.div`
  display: flex;
  width: 350px;
  margin: 0 auto;
  justify-content: space-between;
  padding: 0 5px;
  transform: translateY(120px);
  & > button {
    color: #fff;

    &:hover {
      background-color: #2A416B;
      opacity: 0.7;
    }
  }
`;

const Arrow = styled.button`
  display: flex; 
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  margin: 0 5px;
  cursor: pointer;
  transition: all .3s;
  border-radius: 50%;
  border: none;
  line-height: normal;
  background-color: ${({ background }) => background};

  &:hover {
    border-radius: 50%;
    background-color: #EBEBF0;
    box-shadow: rgba(99, 99, 99, 0.3) 0 2px 6px 0px;
  }

  &:disabled {
     opacity: 0.3 !important;
     cursor: not-allowed;
     box-shadow: none;
  }
`;

// End of styles

/**
 * This component is for preventing the details of materials
 *
 * @author Alireza Zebardast
 * @param {object} plugin
 * @param {object} config
 * @param {object} location
 */
const MaterialDetails = ({ plugin, config, props }) => {
  const [depotLinks, setDepotLinks] = useState([]);
  const [trimmedDetailLinks, setTrimmedDetailLinks] = useState([]);
  const sliderRef = useRef();
  const { projectId } = plugin;
  const { selectedLang, districtId, localityId } = config;

  const { location } = props;
  const { matId } = location.state;

  const { details, error, loading } = useMaterialDetails(
    localityId, matId, selectedLang
  );

  /**
     * Event tracker
     */
  useEffect(() => {
    if (details && details.id) {
      eventTracker('CMS - Material Page', details.id);
    }
  }, [details]);

  useEffect(() => {
    if (
      details
      && Object.keys(details).length
    ) {
      const tempDetailLinks = [...details.links];

      const depotEventsObjs = tempDetailLinks
        .filter(({ href, hrefPart, type }) => ((
          href
          && href.toLowerCase().includes('drop-depot')
        ) || (
            href
            && href.toLowerCase().includes('drop-event')
          ) || (
            hrefPart
            && hrefPart.toLowerCase().includes('drop-depot')
          ) || (
            hrefPart
            && hrefPart.toLowerCase().includes('drop-event')
          )) && type === 'remote');
      setDepotLinks(depotEventsObjs);

      for (let i = 0; i < depotEventsObjs.length; i += 1) {
        const index = tempDetailLinks.findIndex((item) => item === depotEventsObjs[i]);
        tempDetailLinks.splice(index, 1);
      }

      setTrimmedDetailLinks(tempDetailLinks);
    }
  }, [details]);

  const imageUrl = (path) => apis.imageUrl(path);
  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    touchThreshold: 1
  };
  const renderImage = () => {
    if (!details.images.length) return null;
    if (details.images.length > 1) {
      return (
        <Wrapper>
          <Slick ref={sliderRef} {...settings}>
            {
              details.images.map(({ path, thumb }, index) => (
                <ImageContainer key={`${path}-${thumb}`}>
                  <ProgressImage
                    id={index}
                    src={imageUrl(details.images[index].path)}
                    onDragStart={(e) => e.preventDefault}
                  />
                </ImageContainer>
              ))
            }
          </Slick>
        </Wrapper>
      );
    }
    return (<Img src={imageUrl(details.images[0].path)} alt={`${details.name} image`} />);
  };

  return (
    <Container>
      {details?.images?.length > 1 && (
        <ArrowsContainer>
          <Arrow
            background="#2A416B"
            onClick={() => sliderRef.current.slickPrev()}
            aria-label="Next Image"
          >
            &#8678;
          </Arrow>
          <Arrow
            background="#2A416B"
            onClick={() => sliderRef.current.slickNext()}
            aria-label="Previous Image"
          >
            &#8680;
          </Arrow>
        </ArrowsContainer>
      )}
      {
        loading ? (<LoadingSpinner height={500} />) : (
          <>
            {
              !error
                && details
                && Object.keys(details).length
                ? (
                  <ContentContainer>
                    {/* <CTAofCMS /> */}
                    {details.images
                      && details.images.length
                      ? (
                        // <Image images={details.images} alt={`${details.name} image`} />
                        <ImageWrapper>
                          {
                            renderImage()
                          }
                        </ImageWrapper>
                      ) : null}

                    <Container className="content-container-responsive">
                      <Title>{parse(details.disposal_header)}</Title>
                      <Title style={{ marginTop: '0', color: '#595959', fontWeight: '500' }}>{details.name}</Title>
                      <Description>
                        {details.disposal_body}
                      </Description>
                      {
                        details.coll_id
                          || depotLinks.length ? (
                          <DepotContainer>
                            {
                              details.coll_id
                                ? (
                                  <Drawer
                                    link={{ drawer_title: _('collectionRequirements.collection_requirements'), type: 'remote', hrefPart: `collection/${details.coll_id}.page` }}
                                    lang={selectedLang}
                                    flag="collReq"
                                  />
                                ) : null
                            }
                            {
                              depotLinks.map((link) => (
                                <Drawer
                                  key={link.drawer_title}
                                  link={link}
                                  lang={selectedLang}
                                  flag={(link.drawer_title.toLowerCase().includes('locations')) ? 'depot' : 'event'}
                                />
                              ))
                            }
                          </DepotContainer>
                        ) : null
                      }

                      {
                        details.links.length
                          ? (
                            <Drawers>
                              {
                                trimmedDetailLinks.map((link) => (
                                  <li style={{ listStyle: 'none' }}>
                                    <Drawer
                                      key={link.drawer_title}
                                      link={link}
                                      lang={selectedLang}
                                    />
                                  </li>
                                ))
                              }
                            </Drawers>
                          ) : null
                      }
                    </Container>
                  </ContentContainer>
                ) : null
            }
          </>
        )
      }
    </Container>
  );
};

export default MaterialDetails;
